/* eslint @typescript-eslint/no-explicit-any: 0 */

import { clearAllBodyScrollLocks, enableBodyScroll } from 'body-scroll-lock';
import qs from 'qs';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Configure, connectStateResults } from 'react-instantsearch-dom';

import CloseIcon from '~/assets/icons/Close';
import LoadingSpinner from '~/assets/icons/LoadingSpinner';
import StandardModal from '~/components/StandardModal';
import type { ModalCloseReason } from '~/components/StandardModal/types';
import RelatedSearchClickEvent from '~/events/RelatedSearchClickEvent';
import RelatedSearchInteractionEvent from '~/events/RelatedSearchInteractionEvent';
import SearchClearInteractionEvent from '~/events/SearchClearInteractionEvent';
import SearchCloseInteractionEvent, {
  type SearchClosePlacement
} from '~/events/SearchCloseInteractionEvent';
import SearchInputClickEvent from '~/events/SearchInputClickEvent';
import SearchInputInteractionEvent from '~/events/SearchInputInteractionEvent';
import SearchInteractionEvent from '~/events/SearchInteractionEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import { getAppRoot } from '~/utils/dom';
import { getPageViewId } from '~/utils/navigationContext';

import IfHits from './IfHits';
import InstantSearch, { Index } from './InstantSearch';
import SearchBox from './SearchBox';
import SearchPanelHits from './SearchPanelHits';
import SearchPanelRefinedResults from './SearchPanelRefinedResults';
import SearchPanelResultsTypes from './SearchPanelResultsTypes';

type AllResultsType = {
  isLargeBreakpoint: boolean;
  searchResults: any;
  searchState: any;
};

type AllSearchResultsType = {
  clothing: { nbHits: number };
  footwear: { nbHits: number };
  news: { nbHits: number };
};

type SearchPanelProps = {
  defaultToLargeLayout?: boolean;
  isLargeBreakpoint: boolean;
  isMediumBreakpoint: boolean;
  searchPanelState: any;
};

type SearchSuggestionProps = {
  hit: {
    objectID: string;
    query: string;
  };
  isLargeBreakpoint: boolean;
  onClick: (e: unknown) => object;
  position: number;
  searchTerm: string;
};

type SearchPanelResultsProps = {
  close: () => void;
  closing?: boolean;
  displayState: string;
  isLargeBreakpoint: boolean;
  isMediumBreakpoint: boolean;
  onResultCount?: (count: number | undefined) => void;
  query: any;
  setDisplayState: React.Dispatch<React.SetStateAction<string>>;
  setQuery: (query: any) => void;
};

const options = {
  clothing: {
    clickAnalytics: true,
    filters: 'type:product AND sub_type:clothing',
    hitsPerPage: 5
  },
  footwear: {
    clickAnalytics: true,
    filters: 'type:product AND sub_type:sneaker',
    hitsPerPage: 5
  },
  news: {
    clickAnalytics: true,
    filters: 'type:article',
    hitsPerPage: 5
  },
  suggestion: {
    clickAnalytics: true,
    hitsPerPage: 7
  }
};

function isCurrentSearchTerm(suggestion = '', searchTerm = '') {
  return suggestion.trim().toLowerCase() === searchTerm.trim().toLowerCase();
}

function hasRelatedSuggestions({ searchResults, searchTerm }: any) {
  return Boolean(
    searchResults?.hits?.some(
      (hit: any) => !isCurrentSearchTerm(hit.query, searchTerm)
    )
  );
}

function SearchSuggestion({
  hit: { query, objectID },
  isLargeBreakpoint,
  onClick,
  position,
  searchTerm
}: SearchSuggestionProps) {
  const { isBot } = useRootData();
  const user = useUser();

  if (isCurrentSearchTerm(query, searchTerm)) {
    return null;
  }

  function handleRelatedSearchesClick(e: React.MouseEvent<HTMLButtonElement>) {
    const eventData = {
      isBot,
      related_term: query,
      search_term: searchTerm
    };

    const event = new RelatedSearchClickEvent(eventData);
    event.send();

    new RelatedSearchInteractionEvent({
      gesture: 'click',
      guestId: user.guestId,
      isBot,
      placement: 'header',
      position,
      searchTerm: searchTerm || undefined,
      selectedSearchTerm: query
    }).send();

    onClick(e);
  }

  return (
    <li key={objectID} className="flex-none">
      <button
        type="button"
        onMouseDown={(e) => e.preventDefault()}
        onClick={handleRelatedSearchesClick}
        className={`inline-block cursor-pointer rounded-[15px] px-3 py-[6px] text-[13px] leading-[17px] text-[#4d4d4f] transition-colors duration-300 lg:py-[7px] ${
          isLargeBreakpoint ? 'bg-concrete hover:bg-[#dbdbdb]' : 'bg-white'
        }`}
      >
        {query}
      </button>
    </li>
  );
}

const hasSuggestions = hasRelatedSuggestions;

function noSuggestions(props: any) {
  return !hasRelatedSuggestions(props);
}

function noResults({
  allSearchResults
}: {
  allSearchResults: AllSearchResultsType;
}) {
  const {
    footwear: { nbHits: footwearHits } = { nbHits: 0 },
    news: { nbHits: newsHits } = { nbHits: 0 },
    clothing: { nbHits: clothingHits } = { nbHits: 0 }
  } = allSearchResults || {};

  return footwearHits === 0 && newsHits === 0 && clothingHits === 0;
}

function anyResults({
  allSearchResults
}: {
  allSearchResults: AllSearchResultsType;
  searchResults: any;
}) {
  const {
    footwear: { nbHits: footwearHits } = { nbHits: 0 },
    news: { nbHits: newsHits } = { nbHits: 0 },
    clothing: { nbHits: clothingHits } = { nbHits: 0 }
  } = allSearchResults || {};

  return footwearHits > 0 || newsHits > 0 || clothingHits > 0;
}

export const AllResults = connectStateResults(
  ({ isLargeBreakpoint, searchResults, searchState }: AllResultsType) => {
    const results = searchResults && searchResults.nbHits !== 0;

    return results ? null : (
      <div className="my-auto flex h-full flex-col items-center justify-center overflow-auto">
        <p className="m-3 font-heading text-base font-light text-grey-hide">
          {isLargeBreakpoint
            ? `No results for “${searchState.query}”`
            : 'No results'}
        </p>
      </div>
    );
  }
);

const RESULT_SECTIONS = ['footwear', 'news', 'clothing'] as const;

const ResultCount = connectStateResults(
  ({
    allSearchResults,
    onChange,
    searchState,
    searching
  }: {
    allSearchResults: any;
    onChange: (count: number | undefined) => void;
    searchResults: any;
    searchState: any;
    searching?: boolean;
  }) => {
    const query = searchState?.query ?? '';
    const sections = RESULT_SECTIONS.map(
      (section) => allSearchResults?.[section]
    );
    const current =
      !searching &&
      sections.every((results) => results && (results.query ?? '') === query);
    const total = current
      ? sections.reduce((sum, results) => sum + (results.nbHits ?? 0), 0)
      : undefined;

    useEffect(() => {
      onChange(total);
    }, [onChange, total]);

    return null;
  }
);

const DesktopResultsShell = connectStateResults(
  ({
    children,
    closing,
    hasQuery,
    searching
  }: {
    children: ReactNode;
    closing?: boolean;
    hasQuery: boolean;
    searching?: boolean;
  }) => {
    const isLoading = hasQuery && Boolean(searching);

    return (
      <div
        className={`relative max-h-[600px] overflow-hidden ${
          closing
            ? 'animate-search-results-collapse'
            : 'animate-search-results-expand'
        }`}
      >
        <div
          className={`flex max-h-[600px] min-h-[200px] flex-col overflow-y-auto transition-opacity delay-100 duration-200 ease-out [&>*]:flex-auto ${
            isLoading ? 'pointer-events-none opacity-0' : ''
          }`}
        >
          {children}
        </div>
        <div
          className={`pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity delay-100 duration-200 ease-out ${
            isLoading ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden={!isLoading}
        >
          <LoadingSpinner />
        </div>
      </div>
    );
  }
);

export function SearchPanelResults({
  close = () => {},
  closing,
  onResultCount = () => {},
  setQuery = () => {},
  query,
  displayState,
  setDisplayState,
  isLargeBreakpoint,
  isMediumBreakpoint
}: SearchPanelResultsProps) {
  const searchLocationFootwear = {
    pathname: '/release-dates/',
    search: qs.stringify({ q: query })
  };
  const searchLocationNews = {
    pathname: '/news/',
    search: qs.stringify({ q: query })
  };

  const searchLocationClothing = {
    pathname: '/clothing/',
    search: qs.stringify({ q: query })
  };

  const redirectLinks = {
    clothing: searchLocationClothing,
    footwear: searchLocationFootwear,
    news: searchLocationNews
  };

  const content = (
    <div className="content-width my-auto flex size-full flex-col gap-0 pb-0 lg:gap-4 lg:pb-10 lg:pt-6">
      <ResultCount onChange={onResultCount} />
      <Index indexName="search_suggestions">
        <Configure {...options.suggestion} />
        <IfHits condition={hasSuggestions} searchTerm={query}>
          <ul className="no-scrollbar -mx-4 flex flex-nowrap gap-[6px] overflow-x-auto overflow-y-hidden px-4 py-3 md:pb-6 lg:flex-wrap lg:items-center lg:justify-center lg:overflow-hidden lg:pb-4">
            <span className="hidden text-[14px] lg:block">
              Related searches
            </span>
            <SearchPanelHits
              hitComponent={SearchSuggestion}
              hitProps={{
                isLargeBreakpoint,
                onClick: (e: any) => {
                  setQuery(e.target.innerText || e.target.textContent);
                },
                searchTerm: query
              }}
            />
          </ul>
        </IfHits>
        <IfHits condition={noSuggestions} searchTerm={query}>
          <div className="h-3 lg:h-px" />
        </IfHits>
      </Index>

      {!isMediumBreakpoint && !isLargeBreakpoint && (
        <ul className="flex">
          <SearchPanelRefinedResults
            displayState={displayState}
            setDisplayState={setDisplayState}
            searchCondition={anyResults}
            redirectLinks={redirectLinks}
            searchOptions={options}
          />
        </ul>
      )}

      <IfHits condition={noResults}>
        <AllResults isLargeBreakpoint={isLargeBreakpoint} />
      </IfHits>

      <div
        data-search-panel-results
        className={`w-full md:flex md:flex-nowrap md:gap-8 md:[&>div]:w-1/3 ${
          isLargeBreakpoint ? '' : 'min-h-0 flex-1 overflow-y-auto'
        }`}
      >
        <SearchPanelResultsTypes
          redirectLinks={redirectLinks}
          searchOptions={options}
          isLargeBreakpoint={isLargeBreakpoint}
          isMediumBreakpoint={isMediumBreakpoint}
          displayState={displayState}
          close={close}
          query={query}
          searchCondition={anyResults}
        />
      </div>
    </div>
  );

  return isLargeBreakpoint ? (
    <DesktopResultsShell closing={closing} hasQuery={Boolean(query)}>
      {content}
    </DesktopResultsShell>
  ) : (
    content
  );
}

const CLOSE_MS = 300;

function dismissalGesture(placement: SearchClosePlacement) {
  switch (placement) {
    case 'escape_key':
      return 'keypress';
    case 'search_box':
      // A form submit doesn't say whether Enter or the search icon fired it.
      return undefined;
    default:
      return 'click';
  }
}

// Module-level because the header mounts two panels on mobile (the CSS-hidden desktop one and the
// modal) sharing one `active`, and either may become the visible one when the viewport crosses lg.
// One activation must send one search_input.
let activationReported = false;

function getSearchPanelModifiers(isLarge: boolean, isActive: boolean) {
  return [
    isLarge ? 'large' : 'small',
    'primary',
    isLarge && isActive ? 'large-active' : '',
    !isLarge ? 'increase-input-size' : ''
  ];
}

export default function SearchPanel({
  searchPanelState,
  isLargeBreakpoint,
  isMediumBreakpoint,
  defaultToLargeLayout = false
}: SearchPanelProps) {
  const { isBot } = useRootData();
  const user = useUser();
  const scrollTargetElement = getAppRoot();
  const [active, setActive] = searchPanelState;
  const [searchState, setSearchState] = useState({});
  const [displayState, setDisplayState] = useState('footwear');
  const [modifiers, setModifiers] = useState<string[]>(() =>
    getSearchPanelModifiers(isLargeBreakpoint || defaultToLargeLayout, active)
  );
  const [closing, setClosing] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>();
  const closeTimeout = useRef<ReturnType<typeof setTimeout>>();
  const panelRef = useRef<HTMLDivElement>(null);
  // Left unset by a search or a result click: those exits are recorded as their own events, so
  // they send no search_close.
  const closeReason = useRef<SearchClosePlacement>();
  // A dismissal is sent only while the page is still this view. A backdrop click on a link
  // navigates before the closing animation ends, and Back / Forward moves the view without
  // `active` changing; either would send a dismissal with no matching search_input.
  const activationViewId = useRef<string>();
  // Captured when the dismissal starts: a response landing during the closing animation would
  // otherwise report results the shopper never saw.
  const closeResultCount = useRef<number>();
  const resultCount = useRef<number>();
  const onResultCount = useCallback((count: number | undefined) => {
    resultCount.current = count;
  }, []);

  function setQuery(query: any) {
    setSearchState({ ...searchState, query });
  }

  const { query } = searchState as any;

  const searchResultsPath = '/release-dates/';
  const isLargeLayout = modifiers.includes('large');
  const isLargeActive = modifiers.includes('large-active');

  function finaliseClose() {
    closeTimeout.current = undefined;
    setActive(false);
    setClosing(false);
    setModifiers(
      getSearchPanelModifiers(isLargeBreakpoint || defaultToLargeLayout, false)
    );
    clearAllBodyScrollLocks();
    if (scrollTargetElement) enableBodyScroll(scrollTargetElement);

    const reason = closeReason.current;
    const dismissedWith = closeResultCount.current;
    closeReason.current = undefined;
    closeResultCount.current = undefined;

    // Sent here, not when the close starts: clicking back into the box during the animation
    // cancels the close, and that is not a dismissal.
    if (reason && activationViewId.current === getPageViewId()) {
      new SearchCloseInteractionEvent({
        gesture: dismissalGesture(reason),
        guestId: user.guestId,
        isBot,
        placement: reason,
        resultCount: dismissedWith,
        searchTerm: query || undefined
      }).send();
    }
  }

  function closePanel(reason?: SearchClosePlacement) {
    if (closeTimeout.current) {
      // A search or result click during a dismissal's animation replaces the dismissal.
      if (!reason) closeReason.current = undefined;

      return;
    }

    closeReason.current = reason;
    closeResultCount.current = reason ? resultCount.current : undefined;
    setClosing(true);

    if (isLargeBreakpoint) {
      setModifiers(['large', 'primary', '', '']);
    }

    closeTimeout.current = setTimeout(finaliseClose, CLOSE_MS);
  }

  function searchPanelClosed() {
    return closePanel();
  }

  function searchPanelDismissed(reason: SearchClosePlacement) {
    return closePanel(reason);
  }

  function cancelPendingClose() {
    if (closeTimeout.current) {
      clearTimeout(closeTimeout.current);
      closeTimeout.current = undefined;
    }

    closeReason.current = undefined;
    closeResultCount.current = undefined;
    setClosing(false);
  }

  function handleSearchInteraction() {
    cancelPendingClose();
    setActive(true);
    new SearchInputClickEvent({
      isBot
    }).send();
  }

  function searchBoxClicked() {
    cancelPendingClose();
    setActive(true);
    setModifiers(
      getSearchPanelModifiers(isLargeBreakpoint || defaultToLargeLayout, true)
    );
  }

  const resultsProps = {
    close: searchPanelClosed,
    closing,
    displayState,
    onResultCount,
    query,
    setDisplayState,
    setQuery
  };

  // Only the instance on screen for the current breakpoint reports; see activationReported.
  const isVisiblePanel = isLargeBreakpoint === defaultToLargeLayout;

  useEffect(() => {
    if (!active) {
      activationReported = false;

      return;
    }

    if (!isVisiblePanel || activationReported) {
      return;
    }

    activationReported = true;
    activationViewId.current = getPageViewId();

    new SearchInputInteractionEvent({
      gesture: 'focus',
      guestId: user.guestId,
      isBot,
      placement: 'header',
      searchTerm: query || undefined
    }).send();
  }, [active, isBot, isVisiblePanel, query, user.guestId]);

  useEffect(() => {
    return () => {
      if (closeTimeout.current) {
        clearTimeout(closeTimeout.current);
      }

      clearAllBodyScrollLocks();
      if (scrollTargetElement) enableBodyScroll(scrollTargetElement);
    };
  }, [scrollTargetElement]);

  useEffect(() => {
    if (!active || isLargeBreakpoint) {
      return;
    }

    const animatedContainer = panelRef.current;
    const resultsContainer = panelRef.current?.querySelector(
      '[data-search-panel-results]'
    );

    if (!(resultsContainer instanceof HTMLElement)) {
      return;
    }

    resultsContainer.scrollTop = 0;

    const resetScroll = () => {
      resultsContainer.scrollTop = 0;
    };

    animatedContainer?.addEventListener('animationend', resetScroll);

    return () =>
      animatedContainer?.removeEventListener('animationend', resetScroll);
  }, [active, isLargeBreakpoint]);

  useEffect(() => {
    setModifiers(
      getSearchPanelModifiers(isLargeBreakpoint || defaultToLargeLayout, active)
    );
  }, [isLargeBreakpoint, active, defaultToLargeLayout]);

  return (
    <div
      ref={panelRef}
      className={`pt-5 lg:pb-5 ${
        isLargeLayout
          ? 'lg:w-[200px] min-[1200px]:w-[270px]'
          : `flex h-dvh flex-col overflow-hidden ${
              closing
                ? 'animate-search-panel-collapse'
                : 'animate-search-panel-expand'
            }`
      }`}
    >
      <InstantSearch
        searchState={searchState}
        onSearchStateChange={setSearchState}
        indexName="search_suggestions"
      >
        <div className={`shrink-0 ${active ? 'block' : 'hidden'} lg:block`}>
          <div className="content-width flex items-center justify-between gap-2 pr-2 lg:px-0 lg:pr-4">
            <div
              role="presentation"
              onClick={searchBoxClicked}
              className="flex-1"
            >
              <a
                href={`${searchResultsPath}?q=${encodeURIComponent(query)}`}
                ref={linkRef as any}
                className="hidden"
              >
                Redirect to search query
              </a>
              <SearchBox
                isLargeActive={isLargeActive}
                isLargeLayout={isLargeLayout}
                shouldFocusOnMount={active}
                onFocus={handleSearchInteraction}
                onReset={() => {
                  // This closure still holds the term being cleared.
                  if (!query) return;

                  new SearchClearInteractionEvent({
                    gesture: 'click',
                    guestId: user.guestId,
                    isBot,
                    placement: 'header',
                    searchTerm: query
                  }).send();
                }}
                onSubmit={(e) => {
                  e.preventDefault();

                  const navigated = Boolean(linkRef.current && query);

                  if (navigated) {
                    linkRef.current?.click();
                  }

                  if (query) {
                    new SearchInteractionEvent({
                      destinationPath: navigated
                        ? `${searchResultsPath}?q=${encodeURIComponent(query)}`
                        : undefined,
                      gesture: 'click',
                      guestId: user.guestId,
                      isBot,
                      placement: 'search_box',
                      searchTerm: query
                    }).send();

                    searchPanelClosed();
                  } else {
                    // An empty submit only closes the panel, so it counts as a dismissal.
                    searchPanelDismissed('search_box');
                  }
                }}
              />
            </div>
            <div className="lg:hidden">
              <button
                type="button"
                aria-label="Close search"
                onClick={() => searchPanelDismissed('close_button')}
                className="tap-target flex size-6 items-center justify-center after:left-[calc(50%+2px)]"
              >
                <CloseIcon width="12" height="12" stroke="black" />
              </button>
            </div>
          </div>
        </div>

        {active && (
          <>
            {isLargeBreakpoint ? (
              <StandardModal
                backdrop={closing ? 'fade-out' : 'fade-in'}
                closeButtonMotion={closing ? 'spin-out' : 'spin-in'}
                onCloseClick={(_event: unknown, reason: ModalCloseReason) =>
                  searchPanelDismissed(reason)
                }
                isFullWidth
                preventBodyScrollLock={false}
                disableClickOutside={false}
                hideClose={false}
                isFullBody={false}
              >
                <SearchPanelResults
                  {...resultsProps}
                  isLargeBreakpoint={isLargeBreakpoint}
                  isMediumBreakpoint={isMediumBreakpoint}
                />
              </StandardModal>
            ) : (
              <SearchPanelResults
                {...resultsProps}
                isLargeBreakpoint={isLargeBreakpoint}
                isMediumBreakpoint={isMediumBreakpoint}
              />
            )}
          </>
        )}
      </InstantSearch>
    </div>
  );
}
